import { useCallback, useEffect } from 'react'
import { useRoles } from '@/hooks/useRoles'
import { useSWRAxios } from '@/hooks/useSWRAxios'
import type { IEntitySimplified } from '@/modules/Entity/Entity'
import type { ILinkedShareholder } from '@/modules/LinkedShareholder/LinkedShareholder'
import { useAuthContext } from '@/providers/Global'
import { SHAREHOLDER_ROLES } from '@/utils/authorities'

interface ILinkedShareholderListResponse {
  data: ILinkedShareholder[]
  totalCount: number
}

interface IUseGlobalShareholder {
  links: ILinkedShareholder[]
  current?: IEntitySimplified
  isLoading: boolean
  change: (shareholder: IEntitySimplified) => void
}

export const useGlobalShareholder = (): IUseGlobalShareholder => {
  const Auth = useAuthContext()
  const { hasAuthority } = useRoles()

  const mayHaveShareholderAccess = SHAREHOLDER_ROLES.some((role) => hasAuthority(role))

  const { data: response, isLoading } = useSWRAxios<ILinkedShareholderListResponse>(
    mayHaveShareholderAccess ? '/api/user/profile/selectableShareholders' : false,
    { revalidateOnFocus: false },
    true,
  )

  const username = Auth.active?.username
  const current = Auth.active?.globalShareholder

  const links = response?.data?.data ?? []

  const change = useCallback(
    (shareholder: IEntitySimplified) => {
      if (username) {
        Auth.updateGlobalShareholder(username, shareholder)
      }
    },
    [Auth, username],
  )

  useEffect(() => {
    if (!username || !response) {
      return
    }

    const list = response.data.data

    if (list.length === 0) {
      if (current) {
        Auth.updateGlobalShareholder(username, undefined)
      }

      return
    }

    const stillSelected = current ? list.some((link) => link.entity.id === current.id) : false

    if (stillSelected) {
      return
    }

    Auth.updateGlobalShareholder(username, list[0]!.entity)
  }, [Auth, username, response, current])

  return { links, current, isLoading, change }
}
